/**
 * ONE CALL, AS THE PHONE SYSTEM TELLS IT (ops #13). `docs/seams/phone.md` section 7.4 is the design.
 *
 * OPS'S OWN FACTS FIRST, AT ONCE. Whose call it was, when, which way, how it ended, how long, the
 * transcript ops appended, and what reached ops: all from the first answer, drawn before anything
 * is heard from the phone system, because none of it needs the phone system at all.
 *
 * THEN THE PHONE SYSTEM, ASKED BEHIND THE ANSWER. The server reads the call in the background and
 * never inside this request (isolation rule 5), so the first answer says "asked" and this page polls
 * a door that never makes the server read, at 1.5, 2, 3, 4, 5 and 6 seconds, twenty-one and a half
 * in all, past the marker's twenty, and stops. Ask again is offered by the server's one rule and no
 * other: once its `askAgainInSeconds` has run, and at once when the polls found nothing.
 *
 * WHAT IS NEVER HERE. No audio: a recording is a link to the phone system's own page, where a person
 * signs in and listens, and only when the server passed that link through. No markup from the phone:
 * every string is a child of an element and never `dangerouslySetInnerHTML`. Nothing kept: the names
 * in the answer are personal data of the people on the line, and this page holds them in its state
 * while it is open and writes them nowhere.
 *
 * THE OWNERS' AND THE MANAGERS'. The server refuses everybody else on all three doors; a refusal is
 * drawn as one, and a call ops forgot ends the page wherever it is met, at the first door or a poll.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ApiError,
  askPhoneAgain,
  getCall,
  pollCall,
  type CallPage,
  type OpsCall,
  type PhoneCallAnswer,
  type PhoneMoment,
  type PhoneView,
} from '../api.ts';
import { Card, Pill, READING_WIDTH, ScreenTitle } from '../components/AppShell.tsx';
import { Button } from '../components/Field.tsx';
import { Busy, ErrorNotice } from '../components/Notice.tsx';
import { GapPill } from '../components/StatusPills.tsx';
import { callLengthWords, formatInstant } from '../format.ts';
import { CALL_OUTCOME_LABELS } from '../labels.ts';

/**
 * The gaps between the polls, in milliseconds: twenty-one and a half seconds in all, just past the
 * twenty the server's marker stands. Six polls a view, and never a seventh.
 */
export const POLL_DELAYS_MS = [1500, 2000, 3000, 4000, 5000, 6000] as const;

/** The actor of a moment, in words; a word the phone system adds later is shown as it came. */
const ACTOR_WORDS: Readonly<Record<string, string>> = {
  line: 'The line',
  owner: 'A handset',
  recording: 'The recording',
  note: 'Machinery',
};

/** A refusal from ops's own door, which ends the page rather than a part of it. */
const endsThePage = (error: unknown): error is ApiError =>
  error instanceof ApiError && [403, 404, 410].includes(error.status);

const waiting = (view: PhoneView | null) =>
  view === null || view.state === 'asked' || view.state === 'nothing_yet';

export function CallDetail() {
  const { callSid = '' } = useParams();
  const [page, setPage] = useState<CallPage | null>(null);
  const [refused, setRefused] = useState<ApiError | null>(null);
  const [phone, setPhone] = useState<PhoneView | null>(null);
  /** The polls ran out with nothing found. */
  const [gaveUp, setGaveUp] = useState(false);
  /** The server would read again if asked. */
  const [askable, setAskable] = useState(false);
  const [pressFailed, setPressFailed] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  /** Every timer set for this call, so leaving it or asking again clears them all. */
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  /** Which round of asking is current; an answer from an older round is dropped. */
  const round = useRef(0);

  const clearTimers = () => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
  };
  const after = (ms: number, work: () => void) => {
    timers.current.push(setTimeout(work, ms));
  };

  /** What a door said of the phone, and what follows it. */
  function settle(view: PhoneView, mine: number) {
    if (mine !== round.current) return;
    setPhone(view);
    setGaveUp(false);
    setAskable(false);
    if (view.state === 'asked' || view.state === 'nothing_yet') {
      pollFrom(0, mine);
      return;
    }
    // Neither ever reads, so neither starts a poll or offers Ask again.
    if (view.state === 'not_configured' || view.state === 'preview') return;
    if (view.askAgainInSeconds <= 0) setAskable(true);
    else {
      after(view.askAgainInSeconds * 1000, () => {
        if (mine === round.current) setAskable(true);
      });
    }
  }

  /** The polls, one after another, each only once the one before has answered. */
  function pollFrom(index: number, mine: number) {
    const delay = POLL_DELAYS_MS[index];
    if (delay === undefined) {
      if (mine !== round.current) return;
      setGaveUp(true);
      setAskable(true);
      return;
    }
    after(delay, () => {
      pollCall(callSid)
        .then((answer) => {
          if (mine !== round.current) return;
          if (waiting(answer.phone)) pollFrom(index + 1, mine);
          else settle(answer.phone, mine);
        })
        .catch((thrown: unknown) => {
          if (mine !== round.current) return;
          if (endsThePage(thrown)) setRefused(thrown);
          else pollFrom(index + 1, mine);
        });
    });
  }

  useEffect(() => {
    round.current += 1;
    const mine = round.current;
    clearTimers();
    setPage(null);
    setRefused(null);
    setPhone(null);
    setGaveUp(false);
    setAskable(false);
    setPressFailed(null);
    getCall(callSid)
      .then((answer) => {
        if (mine !== round.current) return;
        setPage(answer);
        settle(answer.phone, mine);
      })
      .catch((thrown: unknown) => {
        if (mine !== round.current) return;
        setRefused(
          thrown instanceof ApiError ? thrown : new ApiError(0, 'error', 'The call did not load.'),
        );
      });
    return () => {
      round.current += 1;
      clearTimers();
    };
    // `settle` and `pollFrom` read `callSid` and the two refs, and change with nothing else.
  }, [callSid, attempt]);

  const askAgain = () => {
    round.current += 1;
    const mine = round.current;
    clearTimers();
    setAskable(false);
    setGaveUp(false);
    setPressFailed(null);
    askPhoneAgain(callSid)
      .then((answer) => settle(answer.phone, mine))
      .catch((thrown: unknown) => {
        if (mine !== round.current) return;
        if (endsThePage(thrown)) {
          setRefused(thrown);
          return;
        }
        setPressFailed(
          thrown instanceof ApiError ? thrown.message : 'The press did not reach ops.',
        );
        setAskable(true);
      });
  };

  if (refused) {
    return (
      <div className={READING_WIDTH}>
        <ScreenTitle title="A call" />
        <Refusal error={refused} onRetry={() => setAttempt((n) => n + 1)} />
        <p className="mt-3">
          <Link
            to="/tasks/calls"
            className="inline-flex min-h-11 desk:min-h-9 items-center text-sm underline underline-offset-2"
          >
            Back to the calls
          </Link>
        </p>
      </div>
    );
  }

  if (!page) return <Busy what="the call" />;

  return (
    <div className={READING_WIDTH}>
      <ScreenTitle
        title={`Call from ${whose(page.call)}`}
        subtitle="What ops holds, then what the phone system says."
      />
      <div className="flex flex-col gap-4">
        <OpsFacts page={page} />
        <Card>
          <h2 className="font-serif text-lg leading-7">From the phone system</h2>
          <PhoneSays
            view={phone}
            call={page.call}
            gaveUp={gaveUp}
            askable={askable}
            onAsk={askAgain}
          />
          {pressFailed ? (
            <p className="mt-2 text-sm text-coral" role="alert">
              {pressFailed}
            </p>
          ) : null}
        </Card>
      </div>
    </div>
  );
}

function whose(call: OpsCall): string {
  if (call.kind === 'household') return call.clientName ?? 'a household';
  return call.enquiryName ?? 'an enquiry';
}

/** Ops's own refusal, in words that say which. */
function Refusal({ error, onRetry }: { error: ApiError; onRetry: () => void }) {
  if (error.status === 410) {
    return <ErrorNotice title="This call was forgotten" message={error.message} />;
  }
  if (error.status === 404) return <ErrorNotice title="No such call" message="No such call." />;
  if (error.status === 403) {
    return (
      <ErrorNotice
        title="Not yours to open"
        message="This page is for the owners and the managers."
      />
    );
  }
  return <ErrorNotice title="The call did not load" message={error.message} onRetry={onRetry} />;
}

/* ------------------------------------------------------------------ *
 * What ops holds.
 * ------------------------------------------------------------------ */

const nobodySpokeToHer = (outcome: OpsCall['outcome']) =>
  outcome === 'missed' || outcome === 'abandoned' || outcome === 'voicemail';

function OpsFacts({ page }: { page: CallPage }) {
  const { call, receipts } = page;
  const length = callLengthWords(call.durationSeconds);
  return (
    <Card>
      <h2 className="font-serif text-lg leading-7">What ops holds</h2>
      {call.kind === 'household' ? (
        <>
          <p className="text-sm text-navy/75">
            {[
              call.direction === 'outbound' ? 'We rang them' : 'They rang us',
              length,
              call.contactName,
            ]
              .filter(Boolean)
              .join(', ')}
          </p>
          <p className="mt-0.5 text-xs text-navy/65">{formatInstant(call.occurredAt)}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {!call.outcome ? (
              <Pill>Still on the call</Pill>
            ) : nobodySpokeToHer(call.outcome) ? (
              <GapPill>{CALL_OUTCOME_LABELS[call.outcome]}</GapPill>
            ) : (
              <Pill tone="sage">{CALL_OUTCOME_LABELS[call.outcome]}</Pill>
            )}
          </div>
          {call.notes && call.notes.trim() !== '' ? (
            <>
              <h3 className="mt-3 text-xs tracking-wide text-navy/55 uppercase">
                The words ops keeps from it
              </h3>
              <p className="mt-1 text-base whitespace-pre-wrap wrap-anywhere">{call.notes}</p>
            </>
          ) : null}
          {call.clientId ? (
            <p className="mt-2">
              <Link
                to={`/clients/${call.clientId}`}
                className="inline-flex min-h-11 desk:min-h-9 items-center text-sm underline underline-offset-2"
              >
                Open the household
              </Link>
            </p>
          ) : null}
        </>
      ) : (
        <>
          <p className="text-sm text-navy/75">
            A call from somebody the client book does not know, so it is an enquiry and ops keeps no
            call row for it: what ops holds is the enquiry and the facts below.
          </p>
          <p className="mt-2">
            <Link
              to="/enquiries"
              className="inline-flex min-h-11 desk:min-h-9 items-center text-sm underline underline-offset-2"
            >
              Open the enquiries
            </Link>
          </p>
        </>
      )}

      <h3 className="mt-3 text-xs tracking-wide text-navy/55 uppercase">What reached ops</h3>
      {receipts.length === 0 ? (
        <p className="mt-1 text-sm text-navy/70">Nothing yet.</p>
      ) : (
        <ul className="mt-1 flex flex-col gap-0.5 text-sm">
          {receipts.map((receipt) => (
            <li key={receipt.id}>
              <span className="font-medium">{receipt.type}</span>,{' '}
              {formatInstant(receipt.receivedAt)}
              {receipt.error ? <span className="text-navy/65">. {receipt.error}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * What the phone system says.
 * ------------------------------------------------------------------ */

function PhoneSays({
  view,
  call,
  gaveUp,
  askable,
  onAsk,
}: {
  view: PhoneView | null;
  call: OpsCall;
  gaveUp: boolean;
  askable: boolean;
  onAsk: () => void;
}) {
  const ask = <AskAgain askable={askable} onAsk={onAsk} />;
  if (view === null || view.state === 'asked' || view.state === 'nothing_yet') {
    return gaveUp ? (
      <>
        <Said>The phone system has not answered yet.</Said>
        {ask}
      </>
    ) : (
      <p className="mt-1 text-sm text-navy/70" role="status">
        Asking the phone system about this call.
      </p>
    );
  }
  switch (view.state) {
    case 'not_configured':
      return <Said>This deployment is not connected to the phone system&apos;s call read.</Said>;
    case 'preview':
      return (
        <Said>
          A version preview never asks the phone system: it cannot keep an answer for the minute
          that limits how often ops asks. Open this call on the environment&apos;s own address to
          see what the phone system says.
        </Said>
      );
    case 'answered':
      return (
        <>
          <Answer
            answer={view.call}
            call={call}
            numberCheck={view.numberCheck}
            receiptsCheck={view.receiptsCheck}
          />
          <p className="mt-3 text-xs text-navy/65">As of {formatInstant(view.at)}.</p>
          {ask}
        </>
      );
    case 'not_found':
      return (
        <>
          <Said>
            The phone system holds no call under this id. What ops holds above is all there is.
          </Said>
          {ask}
        </>
      );
    case 'forgotten':
      return (
        <>
          <Said>
            The phone system forgot this call
            {view.forgottenAt ? ` on ${formatInstant(view.forgottenAt)}` : ''}. Nothing more about
            it can be shown. Ops&apos;s own record goes when the phone system&apos;s fact about the
            forget lands.
          </Said>
          {ask}
        </>
      );
    case 'unavailable':
      return (
        <>
          <Said>
            The phone system could not answer just now
            {view.message ? `: ${view.message}` : ` (${view.code}).`}
          </Said>
          {ask}
        </>
      );
    case 'refused':
      return (
        <>
          <Said>
            The phone system refused ops&apos;s signature ({view.code}). The shared secret differs
            between the two systems, their clocks differ, or the phone system does not yet expect
            the way ops now signs. An owner looks at all three.
          </Said>
          {ask}
        </>
      );
    case 'unreachable':
      return (
        <>
          <Said>
            {view.reason === 'timeout'
              ? 'The phone system did not answer within 15 seconds.'
              : view.reason === 'network'
                ? 'The phone system could not be reached.'
                : `The phone system answered in a shape ops does not read (${view.status ?? 'no status'}).`}
          </Said>
          {ask}
        </>
      );
  }
}

function Said({ children }: { children: ReactNode }) {
  return <p className="mt-1 text-sm">{children}</p>;
}

/** Ask again, offered exactly when the server would read; until then, when it will be. */
function AskAgain({ askable, onAsk }: { askable: boolean; onAsk: () => void }) {
  if (!askable) {
    return (
      <p className="mt-2 text-xs text-navy/65">
        Ask again is offered a minute after the phone system answered.
      </p>
    );
  }
  return (
    <div className="mt-3">
      <Button tone="quiet" onClick={onAsk}>
        Ask again
      </Button>
    </div>
  );
}

function Answer({
  answer,
  call,
  numberCheck,
  receiptsCheck,
}: {
  answer: PhoneCallAnswer;
  call: OpsCall;
  numberCheck: 'same' | 'different' | null;
  receiptsCheck: { readonly unknownToOps: readonly string[]; readonly unnamedByPhone: number };
}) {
  const told = answer.moments.filter((moment) => moment.actor !== 'note');
  const machinery = answer.moments.filter((moment) => moment.actor === 'note');
  return (
    <div className="mt-1 flex flex-col gap-3 text-sm">
      <div>
        <p className="font-medium">{whoTookIt(answer)}</p>
        {answer.outcome ? (
          <p className="text-navy/75">
            How it ended there:{' '}
            {answer.outcome in CALL_OUTCOME_LABELS
              ? CALL_OUTCOME_LABELS[answer.outcome as keyof typeof CALL_OUTCOME_LABELS]
              : answer.outcome}
            {answer.durationSeconds !== null ? `, ${callLengthWords(answer.durationSeconds)}` : ''}
          </p>
        ) : null}
        {answer.callerLastFour ? (
          <p className="text-navy/75">{numberWords(answer.callerLastFour, numberCheck, call)}</p>
        ) : null}
        {answer.notifiedAt ? (
          <p className="text-navy/75">
            The owners were emailed at {formatInstant(answer.notifiedAt)}.
          </p>
        ) : null}
        {answer.outcome === 'voicemail' && answer.acknowledged !== null ? (
          <p className="text-navy/75">
            {answer.acknowledged
              ? "Acknowledged on the phone system's wall."
              : 'Not yet acknowledged there.'}
          </p>
        ) : null}
      </div>

      <div>
        <h3 className="text-xs tracking-wide text-navy/55 uppercase">
          What the phone system heard
        </h3>
        {answer.transcript && answer.transcript.trim() !== '' ? (
          <p className="mt-1 text-base whitespace-pre-wrap wrap-anywhere">{answer.transcript}</p>
        ) : (
          <p className="mt-1 text-navy/70">No transcript.</p>
        )}
        {answer.redacted.length > 0 ? (
          <p className="mt-1 text-xs text-navy/65">
            Deliberately incomplete: {answer.redacted.join(', ')} removed before it left the phone
            system. The audio has the original; a person listens.
          </p>
        ) : null}
      </div>

      <Recording audio={answer.audio} />

      {answer.moments.length > 0 ? (
        <div>
          <h3 className="text-xs tracking-wide text-navy/55 uppercase">
            What happened, second by second
          </h3>
          <Moments moments={told} />
          {machinery.length > 0 ? (
            <details className="mt-2 rounded-lg border border-navy/15 p-2">
              <summary className="min-h-11 desk:min-h-9 cursor-pointer text-sm font-medium">
                Show the machinery ({machinery.length})
              </summary>
              <Moments moments={machinery} />
            </details>
          ) : null}
        </div>
      ) : null}

      <div>
        <h3 className="text-xs tracking-wide text-navy/55 uppercase">
          What the two systems agree on
        </h3>
        <ul className="mt-1 flex flex-col gap-0.5">
          {answer.facts.map((fact, index) => (
            <li key={`${fact.remoteId ?? 'none'}-${index}`}>
              {fact.type ?? 'A fact'} at {formatInstant(fact.occurredAt) ?? 'a time not said'},{' '}
              {fact.remoteId
                ? `receipt ${fact.remoteId}, confirmed at ${formatInstant(fact.confirmedAt) ?? 'a time not said'}`
                : 'no receipt from ops yet'}
            </li>
          ))}
        </ul>
        {receiptsCheck.unknownToOps.length === 0 ? (
          <p className="mt-1 text-navy/75">Ops holds every receipt the phone system names.</p>
        ) : (
          receiptsCheck.unknownToOps.map((id) => (
            <p key={id} className="mt-1 text-navy/75">
              The phone system names receipt {id} that ops does not hold.
            </p>
          ))
        )}
        {receiptsCheck.unnamedByPhone > 0 ? (
          <p className="mt-1 text-navy/75">
            Ops holds {receiptsCheck.unnamedByPhone}{' '}
            {receiptsCheck.unnamedByPhone === 1 ? 'receipt' : 'receipts'} the phone system does not
            name.
          </p>
        ) : null}
      </div>
    </div>
  );
}

function whoTookIt(answer: PhoneCallAnswer): string {
  if (answer.answeredBy) return `${answer.answeredBy} answered`;
  if (answer.outcome === 'answered') {
    return 'Answered, and the phone that took it was not recorded';
  }
  return 'Nobody took the call';
}

function numberWords(lastFour: string, check: 'same' | 'different' | null, call: OpsCall): string {
  const ends = `The caller's number ends ${lastFour}`;
  if (check === 'same') {
    const whoseNumber = call.contactName ? `${call.contactName}'s number` : 'the number';
    return `${ends}, the same last four digits as ${whoseNumber} on file.`;
  }
  if (check === 'different') {
    return `${ends}, not the last four digits of the number ops has on file.`;
  }
  return `${ends}.`;
}

function Recording({ audio }: { audio: PhoneCallAnswer['audio'] }) {
  if (!audio) return null;
  if (!audio.stored) {
    return <p className="text-navy/75">The phone system holds no recording of this call.</p>;
  }
  return (
    <div>
      <p className="text-navy/75">
        {audio.seconds !== null
          ? `A recording of ${audio.seconds} seconds is held by the phone system.`
          : 'A recording is held by the phone system.'}
      </p>
      {audio.page ? (
        <a
          href={audio.page}
          target="_blank"
          rel="noreferrer"
          className="inline-flex min-h-11 desk:min-h-9 items-center underline underline-offset-2"
        >
          Listen in the phone system
        </a>
      ) : audio.pageWithheld ? (
        <p className="text-navy/65">The phone system named a page ops does not link to.</p>
      ) : null}
    </div>
  );
}

function Moments({ moments }: { moments: readonly PhoneMoment[] }) {
  return (
    <ol className="mt-1 flex flex-col gap-1.5">
      {moments.map((moment, index) => (
        <li key={`${moment.at ?? ''}-${index}`} className="flex gap-2">
          <span className="w-12 shrink-0 text-xs text-navy/60 tabular-nums">
            {moment.elapsed !== null ? `+${Math.round(moment.elapsed)}s` : ''}
          </span>
          <span className="min-w-0">
            <span className="font-medium">{moment.title ?? 'A step'}</span>
            {moment.actor ? (
              <span className="ml-2 text-xs text-navy/60">
                {ACTOR_WORDS[moment.actor] ?? moment.actor}
              </span>
            ) : null}
            {moment.detail ? (
              <span className="block whitespace-pre-wrap wrap-anywhere text-navy/75">
                {moment.detail}
              </span>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
